import { IList } from 'annotation-tools/utils';
import { useMemo } from 'react';
import { modelDetailEnabledButtons } from '../../../constants/model-detail-enabled-buttons';
import { viewTexts } from '../../../constants/view-texts';
import { ModelStatus } from '../../../types/enum/model-status';
import { IImageInfoType } from '../../../types/record/image-info';

const vt = viewTexts.pages.modelDetail;

export const useConditionalViewValues = ({
  modelStatus,
  imageList
}: {
  modelStatus: ModelStatus;
  imageList: IList<IImageInfoType>;
}) => {
  const numAnnotated = useMemo<number>(
    () => imageList.count(e => e.isAnnotated),
    [imageList]
  );

  const modelImageDataSummaryMessage = useMemo<string[]>(
    () =>
      vt.modelImageDataSummary({
        numImages: imageList.size,
        numAnnotated,
        training: modelStatus === 'training'
      }),
    [imageList.size, numAnnotated, modelStatus]
  );

  const { editLabels, editModelImages } = modelDetailEnabledButtons(
    modelStatus
  );

  return useMemo<{
    messages: { status: 'default' | 'alert'; messages: readonly string[] };
    descriptions: {
      modelImages: string[];
      executeTraining: string;
    };
    buttonsEnabled: {
      editLabels: boolean;
      editModelImages: boolean;
    };
    executeTrainingButtonLabel: string;
  }>(
    () => ({
      messages: {
        status: modelStatus === 'failed' ? 'alert' : 'default',
        messages: (() => {
          switch (modelStatus) {
            case 'created':
              return vt.messages.noLabelRegistered;
            case 'annotating':
              return vt.messages.annotationInComplete;
            case 'annotated':
              return vt.messages.annotationCompleted;
            case 'training':
              return vt.messages.trainingRunning;
            case 'available':
              return [vt.messages.modelIsAvailable];
            case 'failed':
              return [vt.messages.trainingError];
          }
        })()
      },
      descriptions: {
        modelImages: modelImageDataSummaryMessage,
        executeTraining: (() => {
          switch (modelStatus) {
            case 'created':
              return vt.messages.annotationIncompleteShort;
            case 'annotating':
              return vt.messages.annotationIncompleteShort;
            case 'annotated':
              return vt.messages.annotationCompletedShort;
            case 'training':
              return vt.messages.trainingRunningShort;
            case 'available':
              return '';
            case 'failed':
              return vt.messages.trainingError;
          }
        })()
      },
      buttonsEnabled: { editLabels, editModelImages },
      executeTrainingButtonLabel: vt.section3.common.trainButton
    }),
    [modelStatus, modelImageDataSummaryMessage, editLabels, editModelImages]
  );
};
